import * as apiPublic from "@api/public";
import Button from "@components/button";
import Card from '@components/card';
import DetailsGrid from '@components/detailsGrid';
import Icon from "@components/icon";
import facebookIcon from "@assets/facebook.svg";
import instagramIcon from "@assets/instagram.svg";
import PropTypes from "prop-types";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import moment from 'moment';
import styles from "./Annuncio.module.css";
import { ROUTES, useNavigator } from "@contexts/NavigatorContext";
import { getAdvertisementImageUrl, getStatoAnnuncio } from "@logic/annuncio"
import { getBusinessLogoUrl } from "@logic/user";
import { ACCOUNT_TYPE, checkConstant } from "@logic/constants";
import { selectUser } from '@store/userSlice';
import { useCategories } from "@contexts/CategoriesContext";
import { useSelector } from 'react-redux';

const Annuncio = ({ initialAnnuncio }) => {
  const [annuncio, setAnnuncio] = useState(initialAnnuncio);
  const [indiceImmagineCorrente, setIndiceImmagineCorrente] = useState(0);

  const user = useSelector(selectUser);
  const { navigate, currentRoute, changePageTitle } = useNavigator();
  const { getCategoryDescriptionById } = useCategories();

  const isMyAnnuncio = useMemo(() => (
    annuncio?.user.id === user?.id
  ), [annuncio, user?.id]);

  const isBusinessOwner = checkConstant(ACCOUNT_TYPE.AZIENDA, annuncio?.user?.accountType);
  const statoAnnuncio = useMemo(() => getStatoAnnuncio(annuncio), [annuncio]);

  const displayEmail = useMemo(() => (
    annuncio?.user?.displayEmail || annuncio?.user?.displayMail || ""
  ), [annuncio?.user?.displayEmail, annuncio?.user?.displayMail]);

  const getLinkWithProtocol = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    return `https://${value}`;
  }, []);

  const loadAnnuncio = useCallback(async () => {
    const advertisementIdFromParams = currentRoute.params ? currentRoute.params[0] : null;
    const data = await apiPublic.GetAdvertisement({
      advertisementId: advertisementIdFromParams || initialAnnuncio?.id
    });
    setAnnuncio(data);
    changePageTitle(data.title);
  }, [changePageTitle, currentRoute.params, initialAnnuncio?.id])

  const shareAnnuncio = useCallback(() => {
    navigator.share({
      title: document.title + " | La Soffiata",
      text: annuncio.description,
      url: window.location.href,
    });
  }, [annuncio])

  const foregroundImageSrc = useMemo(() => (
    getAdvertisementImageUrl({
      userId: annuncio?.userId,
      advertisementId: annuncio?.id,
      imageId: annuncio?.images[indiceImmagineCorrente]?.id,
    })
  ), [annuncio, indiceImmagineCorrente]);

  const phones = [annuncio?.user?.tel, annuncio?.user?.cel, annuncio?.user?.cel2].filter(Boolean);
  const phonesText = phones.length > 0 ? `Tel. ${phones.join(" - ")}` : "";

  const businessLogoUrl = useMemo(() => (
    getBusinessLogoUrl({
      userId: annuncio?.user?.id
    })
  ), [annuncio?.user?.id]);

  const imagesCarousel = useMemo(() => {
    return annuncio?.images?.map((x, i) => {
      const src = getAdvertisementImageUrl({
        userId: annuncio.userId,
        advertisementId: annuncio.id,
        imageId: annuncio.images[i].id,
      });

      return (
        <img
          key={x.id}
          src={src}
          alt={`Anteprima immagine annuncio numero ${i + 1}`}
          className={styles.anteprimaImmagine + (i === indiceImmagineCorrente ? " " + styles.active : "")}
          onClick={() => setIndiceImmagineCorrente(i)}
        />
      );
    });
  }, [annuncio, indiceImmagineCorrente]);

  useEffect(() => {
    loadAnnuncio();
  }, [loadAnnuncio]);

  if (!annuncio) {
    return null;
  }

  return (
    <>
      {annuncio.images && annuncio.images.length > 0 && (
        <div className={styles.wrapperImmagini}>
          <img
            src={foregroundImageSrc}
            alt={`Immagine annuncio ${annuncio.title}`}
            className={styles.immagineCorrente}
          />
          <div className={styles.caroselloImmagini}>
            {imagesCarousel}
          </div>
        </div>
      )}
      <br></br>
      <div className='row'>
        <div className='col'>
          <div className={styles.infoPrincipali}>
            <span className={styles.titolo}>{annuncio.title}</span>
            {statoAnnuncio.isPagamentoRichiesto ? (
              <span className={styles.descrizionePagamentoRichiesto}>Pagamento richiesto per pubblicare l'annuncio</span>
            ) : (
              <>
                <span className={styles.descrizione}>{`Annuncio pubblicato il ${moment(annuncio.publishDate).format("D MMMM YYYY")} presso ${annuncio.city} (${annuncio.province})`}</span>
                <span className={styles.descrizione}>{`L'annuncio scadrà il ${moment(annuncio.expirationDate).format("D MMMM YYYY")}`}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {!isMyAnnuncio && (
        <div className='row'>
          <div className='col'>
            <Button
              color="primary"
              fullWidth
              onClick={() => { }}
              text="Invia un messaggio al venditore"
            />
          </div>
        </div>
      )}
      <div className='row'>
        <div className='col'>
          <div className={styles.azioniPrincipali}>
            {(isMyAnnuncio || user.isAdmin) && (
              <Button
                color="primary"
                icon="settings"
                onClick={() => navigate(ROUTES.ANNUNCIO_GESTISCI, [annuncio.id])}
                text="Gestisci"
              />
            )}
            <Button
              icon="share"
              onClick={shareAnnuncio}
              text="Condividi"
            />
          </div>
        </div>
      </div>
      <br></br>
      <div className='row'>
        <div className='col'>
          <Card>
            <div className='row'>
              <div className='col'>
                <span className="page-section">Dettagli</span>
              </div>
            </div>
            <div className='row'>
              <div className='col'>
                <DetailsGrid
                  labels={["Categoria", "Descrizione"]}
                  values={[getCategoryDescriptionById(annuncio.categoryId), annuncio.description]}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
      {isBusinessOwner ? (
        <div className='row'>
          <div className='col'>
            <Card>
              <div className='row'>
                <div className='col'>
                  <span className="page-section">Informazioni sull'azienda</span>
                </div>
              </div>
              <div className={styles.businessInfoWrapper}>
                <div className={styles.businessImagePlaceholder} aria-hidden={businessLogoUrl ? undefined : "true"}>
                  {businessLogoUrl ? (
                    <img
                      src={businessLogoUrl}
                      alt={`Logo ${annuncio?.user?.advertisementName || "azienda"}`}
                      className={styles.businessImage}
                    />
                  ) : (
                    <Icon
                      name="storefront"
                      size={48}
                      fill={1}
                      weight={400}
                    />
                  )}
                </div>
                <div className={styles.businessInfoTextBlock}>
                  <div className={styles.businessRowHeader}>
                    {annuncio.user.advertisementName}
                  </div>
                  <div className={styles.businessRow}>
                    {annuncio.user.address}
                  </div>
                  <div className={styles.businessRow}>
                    {phonesText}
                  </div>
                  <div className={styles.businessRow}>
                    {displayEmail && (<a href={`mailto:${displayEmail}`} className={styles.businessContactLink}>{displayEmail}</a>)}
                  </div>
                  <div className={styles.businessRow}>
                    <a href={getLinkWithProtocol(annuncio?.user?.website)} target="_blank" rel="noopener noreferrer" className={styles.businessSocialLink}>
                      {annuncio?.user?.website}
                    </a>
                  </div>
                  <div className={styles.businessRow}>
                  {annuncio?.user?.facebook && (
                    <a href={getLinkWithProtocol(annuncio?.user?.facebook)} target="_blank" rel="noopener noreferrer" className={styles.businessSocialLink}>
                      <img src={facebookIcon} alt="Facebook" className={styles.businessSocialIcon} />
                    </a>
                  )}
                  {annuncio?.user?.instagram && (
                    <a href={getLinkWithProtocol(annuncio?.user?.instagram)} target="_blank" rel="noopener noreferrer" className={styles.businessSocialLink}>
                      <img src={instagramIcon} alt="Instagram" className={styles.businessSocialIcon} />
                    </a>
                  )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      ) :
      (
        <div className='row'>
          <div className='col'>
            <Card>
              <div className='row'>
                <div className='col'>
                  <span className="page-section">Informazioni sul venditore</span>
                </div>
              </div>
              <div className='row'>
                <div className='col'>
                  <DetailsGrid
                    labels={["Username", "Cellulare", "Telefono"]}
                    values={[annuncio.user.advertisementName, annuncio.user.cel, annuncio.user.tel]}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col'>
                  <Button
                    onClick={() => navigate(ROUTES.UTENTE, [annuncio.user.id])}
                    size="mini"
                    text="Visita il profilo del venditore"
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

Annuncio.propTypes = {
  initialAnnuncio: PropTypes.shape({
    categoryId: PropTypes.number,
    city: PropTypes.string,
    description: PropTypes.string,
    editions: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        id: PropTypes.number,
      })
    ),
    expirationDate: PropTypes.string,
    id: PropTypes.number,
    images: PropTypes.array,
    isFeatured: PropTypes.bool,
    isSuspended: PropTypes.bool,
    province: PropTypes.string,
    publishDate: PropTypes.string,
    title: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      advertisementName: PropTypes.string,
      accountType: PropTypes.number,
      address: PropTypes.string,
      tel: PropTypes.string,
      cel: PropTypes.string,
      cel2: PropTypes.string,
      email: PropTypes.string,
      displayEmail: PropTypes.string,
      displayMail: PropTypes.string,
      facebook: PropTypes.string,
      instagram: PropTypes.string,
      website: PropTypes.string,
      street: PropTypes.string,
      civic: PropTypes.string,
      city: PropTypes.string,
      province: PropTypes.string,
      zipCode: PropTypes.string
    }),
    userId: PropTypes.number,
    validationStatus: PropTypes.number,
  }),
};

Annuncio.defaultProps = {
  annuncio: undefined,
};

export default Annuncio;
